import type { ISubscriptionInfo } from "@/account/account-models";
import type { IGetRequest, IPostRequest } from "@/utils/async";
import { ERequestStatus, requestInit } from "@/utils/async";
import { createReducer } from "@reduxjs/toolkit";
import type {
  IGetBroadcastStatsConfig,
  IGetPayoutsConfig,
  IGetReferralsStatConfig,
  IGetSubscribersConfig,
  IGetUserStatsConfig,
} from "./earnings-action-creators";
import { IEarningsActions } from "./earnings-actions";
import type {
  IAccountInfo,
  IBroadcastStatsItem,
  IPayout,
  IPayoutTotals,
  IReferralsStatSummary,
  IReferralStatsItem,
  IUserStatsItem,
} from "./earnings-models";

export interface IEarningsModel {
  getPayoutTotals: IGetRequest<any>;
  getPayoutStats: IGetRequest<any>;
  getPayouts: IGetRequest<any>;
  nextPayoutsUrl: string | null;
  payouts: IPayout[] | null;
  getAccountInfo: IGetRequest<any>;
  patchPayoutMethod: IGetRequest<any>;
  postPayoutRequest: IPostRequest<any, any>;
  postAntiMoneyLaundering: IPostRequest<any, any>;
  accountInfo: IAccountInfo | null;
  payoutTotals: IPayoutTotals | null;
  getSubscribers: IGetRequest<any>;
  nextSubscribersUrl: string | null;
  subscribers: ISubscriptionInfo[] | null;
  getReferralsStat: IGetRequest<any>;
  nextReferralsStatUrl: string | null;
  referralsStat: IReferralStatsItem[] | null;
  referralsStatSummary: IReferralsStatSummary | null;
  getBroadcastStats: IGetRequest<any>;
  nextBroadcastStatsUrl: string | null;
  broadcastStats: IBroadcastStatsItem[] | null;
  getUserStats: IGetRequest<any>;
  nextUserStatsUrl: string | null;
  userStats: IUserStatsItem[] | null;
}

const INITIAL_STATE: IEarningsModel = {
  getPayoutTotals: requestInit(),
  getPayoutStats: requestInit(),
  getPayouts: requestInit(),
  nextPayoutsUrl: null,
  payouts: null,
  getAccountInfo: requestInit(),
  patchPayoutMethod: requestInit(),
  postPayoutRequest: requestInit(),
  postAntiMoneyLaundering: requestInit(),
  accountInfo: null,
  payoutTotals: null,
  getSubscribers: requestInit(),
  nextSubscribersUrl: null,
  subscribers: null,
  getReferralsStat: requestInit(),
  nextReferralsStatUrl: null,
  referralsStat: null,
  referralsStatSummary: null,
  getBroadcastStats: requestInit(),
  nextBroadcastStatsUrl: null,
  broadcastStats: null,
  getUserStats: requestInit(),
  nextUserStatsUrl: null,
  userStats: null,
};

/** Reducer */
export const earningsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IEarningsActions.initGetPayoutTotals,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutTotals: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successGetPayoutTotals,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutTotals: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          payoutTotals: action.payload,
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetPayoutTotals,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutTotals: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetPayoutStats,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutStats: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successGetPayoutStats,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutStats: {
            responseData: action.payload.data,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetPayoutStats,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayoutStats: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetPayouts,
      (state, action: any): IEarningsModel => {
        // const { isInitialRequest } = action.payload.config;
        return {
          ...state,
          getPayouts: {
            status: ERequestStatus.Fetching,
          },
          // payouts: isInitialRequest ? null : state.payouts
        };
      }
    )
    .addCase(
      IEarningsActions.successGetPayouts,
      (state, action: any): IEarningsModel => {
        const config: IGetPayoutsConfig = action.payload.config;
        const res = action.payload.res;

        let payouts: IPayout[] = res.payouts;
        const getPayoutsNextUrl: string =
          res.payouts.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          payouts = [...(state.payouts || []), ...res.payouts];
        }

        return {
          ...state,
          getPayouts: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
          payouts,
          nextPayoutsUrl: getPayoutsNextUrl,
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetPayouts,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getPayouts: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetAccountInfo,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getAccountInfo: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successGetAccountInfo,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getAccountInfo: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          accountInfo: action.payload,
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetAccountInfo,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getAccountInfo: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initPatchPayoutMethod,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          patchPayoutMethod: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successPatchPayoutMethod,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          patchPayoutMethod: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failurePatchPayoutMethod,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          patchPayoutMethod: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initPostPayoutRequest,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postPayoutRequest: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successPostPayoutRequest,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postPayoutRequest: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failurePostPayoutRequest,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postPayoutRequest: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initPostAntiMoneyLaundering,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postAntiMoneyLaundering: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.successPostAntiMoneyLaundering,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postAntiMoneyLaundering: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failurePostAntiMoneyLaundering,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          postAntiMoneyLaundering: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetSubscribers,
      (state, action: any): IEarningsModel => {
        // const { isInitialRequest } = action.payload.config;
        const nextState = {
          getSubscribers: {
            status: ERequestStatus.Fetching,
          },
          // subscribers: isInitialRequest ? null : state.subscribers
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetSubscribers,
      (state, action: any): IEarningsModel => {
        const config: IGetSubscribersConfig = action.payload.config;
        const res = action.payload.res;

        let subscribers: ISubscriptionInfo[] = res.data;
        const getSubscribersNextUrl: string =
          res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          subscribers = [...(state.subscribers || []), ...res.data];
        }

        return {
          ...state,
          nextSubscribersUrl: getSubscribersNextUrl,
          subscribers,
          getSubscribers: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetSubscribers,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getSubscribers: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetReferralsStat,
      (state, action: any): IEarningsModel => {
        // const { isInitialRequest } = action.payload.config;
        const nextState = {
          getReferralsStat: {
            status: ERequestStatus.Fetching,
          },
          // referralsStat: isInitialRequest ? null : state.referralsStat
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetReferralsStat,
      (state, action: any): IEarningsModel => {
        const config: IGetReferralsStatConfig = action.payload.config;
        const res = action.payload.res;

        let referralsStat: IReferralStatsItem[] = res.data;
        const getReferralsStatNextUrl: string =
          res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          referralsStat = [...(state.referralsStat || []), ...res.data];
        }

        return {
          ...state,
          nextReferralsStatUrl: getReferralsStatNextUrl,
          referralsStat,
          referralsStatSummary: res.summary,
          getReferralsStat: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetReferralsStat,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getReferralsStat: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetBroadcastStats,
      (state, action: any): IEarningsModel => {
        // const { isInitialRequest } = action.payload.config;
        const nextState = {
          getBroadcastStats: {
            status: ERequestStatus.Fetching,
          },
          // broadcastStats: isInitialRequest ? null : state.broadcastStats
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetBroadcastStats,
      (state, action: any): IEarningsModel => {
        const config: IGetBroadcastStatsConfig = action.payload.config;
        const res = action.payload.res;

        let broadcastStats: IBroadcastStatsItem[] = res.data;
        const getBroadcastStatsNextUrl: string =
          res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          broadcastStats = [...(state.broadcastStats || []), ...res.data];
        }

        return {
          ...state,
          nextBroadcastStatsUrl: getBroadcastStatsNextUrl,
          broadcastStats,
          getBroadcastStats: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetBroadcastStats,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getBroadcastStats: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.initGetUserStats,
      (state, action: any): IEarningsModel => {
        const nextState = {
          getUserStats: {
            status: ERequestStatus.Fetching,
          },
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IEarningsActions.successGetUserStats,
      (state, action: any): IEarningsModel => {
        const config: IGetUserStatsConfig = action.payload.config;
        const res = action.payload.res;

        let userStats: IUserStatsItem[] = res.data;
        const getUserStatsNextUrl: string = res.data.length > 0 ? res.next : "";

        if (!config.isInitialRequest) {
          userStats = [...(state.userStats || []), ...res.data];
        }

        return {
          ...state,
          nextUserStatsUrl: getUserStatsNextUrl,
          userStats,
          getUserStats: {
            responseData: action.payload.res,
            status: ERequestStatus.Fetched,
          },
        };
      }
    )
    .addCase(
      IEarningsActions.failureGetUserStats,
      (state, action: any): IEarningsModel => {
        return {
          ...state,
          getUserStats: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    );
});
